/**
 * Product form → "Suggestions" (F-DEAL-N4): what to suggest when this item is
 * added. Saved on its own button, separate from the product's main save.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toastHelpers } from '@/lib/toast-helpers'
import type { UpsellCatalogue, UpsellMode } from '@/types/deals'
import { Field, TargetPicker, UPSELL_ADMIN_QUERY_KEY, useDealsCatalog } from './DealsControls'
import { errorText } from './dealsText'

export function ProductUpsellSection({
  productId,
  categoryId,
  money,
}: {
  productId: string
  categoryId?: string
  money: (v: number) => string
}) {
  const qc = useQueryClient()
  const catalog = useDealsCatalog()
  const query = useQuery({
    queryKey: UPSELL_ADMIN_QUERY_KEY,
    queryFn: async () => (await apiClient.getUpsellAdmin()).data as UpsellCatalogue,
    staleTime: 30_000,
  })
  const data = query.data
  const savedMode: UpsellMode = data?.product_modes?.[productId] ?? 'inherit'
  const savedPrompt = data?.product_prompts?.[productId] ?? ''
  const savedIds = useMemo(() => data?.product_upsells?.[productId] ?? [], [data, productId])
  const [mode, setMode] = useState<UpsellMode>(savedMode)
  const [prompt, setPrompt] = useState(savedPrompt)
  const [ids, setIds] = useState<string[]>(savedIds)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const savedKey = `${savedMode}|${savedPrompt}|${savedIds.join(',')}`
  useEffect(() => {
    setMode(savedMode)
    setPrompt(savedPrompt)
    setIds(savedIds)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey])

  const dirty = mode !== savedMode || prompt.trim() !== savedPrompt.trim() || ids.join(',') !== savedIds.join(',')
  const others = useMemo(() => catalog.all.filter((p) => String(p.id) !== productId), [catalog.all, productId])
  const categoryList = categoryId ? data?.category_upsells?.[categoryId] ?? [] : []
  const categoryPrompt = categoryId ? data?.category_prompts?.[categoryId] ?? '' : ''

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      await apiClient.putProductUpsell(productId, { mode, prompt: prompt.trim(), suggested_product_ids: mode === 'off' ? [] : ids })
      await qc.invalidateQueries({ queryKey: UPSELL_ADMIN_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ['menu-rules'] })
      toastHelpers.success('Suggestions saved')
    } catch (e) {
      setError(errorText(e, 'Could not save suggestions'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-base font-semibold">Suggestions</h3>
        <p className="text-sm text-muted-foreground">What to suggest when this item is added, for example a drink with a burger.</p>
      </div>
      {query.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : query.isError ? (
        <p className="text-sm text-destructive">Could not load suggestions.</p>
      ) : (
        <>
          {data && !data.config.enabled && (
            <p className="text-xs text-muted-foreground">Upsell suggestions are switched off in Settings, so these are not shown yet.</p>
          )}
          <Field label="Suggestions for this item">
            <Select value={mode} onValueChange={(v) => setMode(v as UpsellMode)}>
              <SelectTrigger className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Inherit from category</SelectItem>
                <SelectItem value="on">On</SelectItem>
                <SelectItem value="off">Off — never suggest anything here</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {mode !== 'off' && (
            <>
              <Field label="Prompt (optional)" hint="Up to 80 characters. Empty uses the category’s prompt.">
                <Input value={prompt} maxLength={80} placeholder="Add a drink?" onChange={(e) => setPrompt(e.target.value)} />
              </Field>
              <Field
                label="This item’s own suggestions (up to 4)"
                hint={
                  ids.length > 0
                    ? 'These replace the category’s suggestions for this item.'
                    : categoryList.length > 0
                      ? `Empty, so the category’s are used: ${categoryList.map((id) => catalog.productName(id) ?? 'removed item').join(', ')}${categoryPrompt ? ` (“${categoryPrompt}”)` : ''}.`
                      : 'Empty, and its category has none yet — set them in Menu → Deals → Upsells.'
                }
              >
                <TargetPicker
                  products={others}
                  categories={[]}
                  productIds={ids}
                  categoryIds={[]}
                  allowCategories={false}
                  max={4}
                  addLabel={ids.length === 0 ? 'Add suggestions' : 'Add another'}
                  onChange={(next) => setIds(next)}
                  money={money}
                />
              </Field>
            </>
          )}
          {error && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
          <Button type="button" variant={dirty ? 'default' : 'outline'} disabled={!dirty || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save suggestions'}
          </Button>
        </>
      )}
    </div>
  )
}
